/**
 * iGaming multi-level sub-affiliate (#156) — operator UI.
 *
 * Composition:
 *   - KPI strip: levels configured, max depth, license status, total payout projection.
 *   - Program card: offer lookup + per-level bps editor + license gate toggle.
 *   - Upline card: register affiliate->parent links and inspect the upline chain.
 *   - Cascade card: calculator that runs a dry-run cascade against stored uplines.
 *
 * Brand: emerald->sky headline gradient, zinc borders, 6-8px rounded, light
 * theme only, no emojis, no competitor names.
 *
 * Data layer lives in `./api`; the page itself is purely render + wiring.
 */
import { useCallback, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	calculateCascade,
	commitCascade,
	createProgram,
	defaultLevelBps,
	deleteProgram,
	fetchEvents,
	fetchProgramByOffer,
	fetchUplineChain,
	formatBpsPct,
	formatCents,
	formatInteger,
	formatUnixDate,
	MAX_DEPTH,
	MIN_LEVELS_REQUIRED,
	mlmQueryKeys,
	parseBpsList,
	projectTotalCents,
	setProgramLicense,
	upsertUpline,
	type CalculateResponse,
	type EventsResponse,
	type LevelPayout,
	type Program,
	type UplineChainResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — match the dash brand rules.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function IgamingMultiLevelPage() {
	const queryClient = useQueryClient();
	const [offerId, setOfferId] = useState("");
	const [maxDepth, setMaxDepth] = useState(3);
	const [bpsRaw, setBpsRaw] = useState(defaultLevelBps().join(", "));
	const [licenseOk, setLicenseOk] = useState(false);
	const [active, setActive] = useState(true);

	const [affiliateId, setAffiliateId] = useState("");
	const [parentId, setParentId] = useState("");

	const [producerAffId, setProducerAffId] = useState("");
	const [ngrInput, setNgrInput] = useState("100000");
	const [lastCascade, setLastCascade] = useState<CalculateResponse | null>(null);

	// ---- Queries ----------------------------------------------------------

	const trimmedOffer = offerId.trim();
	const programQuery = useQuery({
		queryKey: mlmQueryKeys.program(trimmedOffer),
		queryFn: () => fetchProgramByOffer(trimmedOffer),
		enabled: trimmedOffer.length > 0,
	});

	const trimmedAff = affiliateId.trim();
	const uplineQuery = useQuery<UplineChainResponse>({
		queryKey: mlmQueryKeys.upline(trimmedAff),
		queryFn: () => fetchUplineChain(trimmedAff),
		enabled: trimmedAff.length > 0,
	});

	const eventsQuery = useQuery<EventsResponse>({
		queryKey: mlmQueryKeys.events({ offer_id: trimmedOffer }),
		queryFn: () =>
			fetchEvents({
				offer_id: trimmedOffer || undefined,
				limit: 10,
			}),
		enabled: trimmedOffer.length > 0,
	});

	const parsedBps = useMemo(() => parseBpsList(bpsRaw), [bpsRaw]);
	const bpsValid = parsedBps.length >= MIN_LEVELS_REQUIRED && parsedBps.length >= maxDepth;

	const ngrCents = useMemo(() => {
		const n = Number.parseInt(ngrInput, 10);
		return Number.isInteger(n) && n > 0 ? n : 0;
	}, [ngrInput]);

	const projectedTotal = useMemo(
		() => projectTotalCents(ngrCents, parsedBps),
		[ngrCents, parsedBps],
	);

	const program: Program | null = programQuery.data?.program ?? null;

	// ---- Mutations --------------------------------------------------------

	const programMutation = useMutation({
		mutationFn: createProgram,
		onSuccess: () => {
			toast.success("Program saved.");
			queryClient.invalidateQueries({
				queryKey: mlmQueryKeys.program(trimmedOffer),
			});
		},
		onError: (err: Error) => toast.error(err.message ?? "Save failed."),
	});

	const licenseMutation = useMutation({
		mutationFn: ({ id, ok }: { id: string; ok: boolean }) =>
			setProgramLicense(id, ok),
		onSuccess: () => {
			toast.success("License updated.");
			queryClient.invalidateQueries({
				queryKey: mlmQueryKeys.program(trimmedOffer),
			});
		},
		onError: (err: Error) => toast.error(err.message ?? "License update failed."),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteProgram,
		onSuccess: () => {
			toast.success("Program deactivated.");
			queryClient.invalidateQueries({
				queryKey: mlmQueryKeys.program(trimmedOffer),
			});
		},
		onError: (err: Error) => toast.error(err.message ?? "Deactivate failed."),
	});

	const uplineMutation = useMutation({
		mutationFn: upsertUpline,
		onSuccess: () => {
			toast.success("Upline saved.");
			queryClient.invalidateQueries({
				queryKey: mlmQueryKeys.upline(trimmedAff),
			});
		},
		onError: (err: Error) => toast.error(err.message ?? "Upline failed."),
	});

	const calcMutation = useMutation({
		mutationFn: calculateCascade,
		onSuccess: (res) => {
			setLastCascade(res);
			toast.success("Cascade computed.");
		},
		onError: (err: Error) => toast.error(err.message ?? "Cascade failed."),
	});

	const commitMutation = useMutation({
		mutationFn: commitCascade,
		onSuccess: () => {
			toast.success("Event committed.");
			queryClient.invalidateQueries({
				queryKey: mlmQueryKeys.events({ offer_id: trimmedOffer }),
			});
		},
		onError: (err: Error) => toast.error(err.message ?? "Commit failed."),
	});

	const busy =
		programMutation.isPending ||
		licenseMutation.isPending ||
		deleteMutation.isPending ||
		uplineMutation.isPending ||
		calcMutation.isPending ||
		commitMutation.isPending;

	// ---- Handlers ---------------------------------------------------------

	const handleSaveProgram = useCallback(() => {
		if (busy) return;
		if (trimmedOffer.length === 0) {
			toast.error("Offer id required.");
			return;
		}
		if (!bpsValid) {
			toast.error(`Need at least ${Math.max(MIN_LEVELS_REQUIRED, maxDepth)} valid level bps.`);
			return;
		}
		programMutation.mutate({
			offer_id: trimmedOffer,
			max_depth: maxDepth,
			level_bps: parsedBps,
			license_ok: licenseOk,
			active,
		});
	}, [busy, trimmedOffer, bpsValid, maxDepth, parsedBps, licenseOk, active, programMutation]);

	const handleLicenseToggle = useCallback(() => {
		if (busy || !program) return;
		licenseMutation.mutate({ id: program.id, ok: !program.license_ok });
	}, [busy, program, licenseMutation]);

	const handleDelete = useCallback(() => {
		if (busy || !program) return;
		if (typeof window !== "undefined") {
			const ok = window.confirm(`Deactivate program for ${program.offer_id}?`);
			if (!ok) return;
		}
		deleteMutation.mutate(program.id);
	}, [busy, program, deleteMutation]);

	const handleSaveUpline = useCallback(() => {
		if (busy) return;
		if (trimmedAff.length === 0) {
			toast.error("Affiliate id required.");
			return;
		}
		const parent = parentId.trim();
		uplineMutation.mutate({
			affiliate_id: trimmedAff,
			parent_id: parent.length > 0 ? parent : null,
		});
	}, [busy, trimmedAff, parentId, uplineMutation]);

	const handleCalculate = useCallback(() => {
		if (busy) return;
		if (trimmedOffer.length === 0 || producerAffId.trim().length === 0) {
			toast.error("Offer + producer ids required.");
			return;
		}
		if (ngrCents <= 0) {
			toast.error("NGR must be a positive integer.");
			return;
		}
		calcMutation.mutate({
			offer_id: trimmedOffer,
			producer_aff_id: producerAffId.trim(),
			ngr_cents: ngrCents,
		});
	}, [busy, trimmedOffer, producerAffId, ngrCents, calcMutation]);

	const handleCommit = useCallback(() => {
		if (busy) return;
		if (trimmedOffer.length === 0 || producerAffId.trim().length === 0) {
			toast.error("Offer + producer ids required.");
			return;
		}
		if (ngrCents <= 0) {
			toast.error("NGR must be a positive integer.");
			return;
		}
		commitMutation.mutate({
			offer_id: trimmedOffer,
			producer_aff_id: producerAffId.trim(),
			ngr_cents: ngrCents,
		});
	}, [busy, trimmedOffer, producerAffId, ngrCents, commitMutation]);

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------
	return (
		<main
			data-testid="igaming-multi-level-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<KpiStrip
				levelsConfigured={parsedBps.length}
				maxDepth={maxDepth}
				licenseOk={program?.license_ok ?? null}
				projectedTotal={projectedTotal}
			/>

			<ProgramCard
				offerId={offerId}
				setOfferId={setOfferId}
				maxDepth={maxDepth}
				setMaxDepth={setMaxDepth}
				bpsRaw={bpsRaw}
				setBpsRaw={setBpsRaw}
				parsedBps={parsedBps}
				bpsValid={bpsValid}
				licenseOk={licenseOk}
				setLicenseOk={setLicenseOk}
				active={active}
				setActive={setActive}
				program={program}
				loading={programQuery.isLoading && trimmedOffer.length > 0}
				busy={busy}
				onSave={handleSaveProgram}
				onLicenseToggle={handleLicenseToggle}
				onDelete={handleDelete}
			/>

			<UplineCard
				affiliateId={affiliateId}
				setAffiliateId={setAffiliateId}
				parentId={parentId}
				setParentId={setParentId}
				data={uplineQuery.data ?? null}
				loading={uplineQuery.isLoading && trimmedAff.length > 0}
				busy={busy}
				onSave={handleSaveUpline}
			/>

			<CascadeCard
				producerAffId={producerAffId}
				setProducerAffId={setProducerAffId}
				ngrInput={ngrInput}
				setNgrInput={setNgrInput}
				ngrCents={ngrCents}
				lastCascade={lastCascade}
				busy={busy}
				onCalculate={handleCalculate}
				onCommit={handleCommit}
			/>

			<EventsCard
				data={eventsQuery.data ?? null}
				loading={eventsQuery.isLoading && trimmedOffer.length > 0}
				hasOffer={trimmedOffer.length > 0}
			/>
		</main>
	);
}

export default IgamingMultiLevelPage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				iGaming
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Multi-level sub-affiliates
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Configure a cascade of commissions for uplines three or more levels
				deep. License-gated per offer: cascade payouts only activate once the
				license flag is set by operations.
			</p>
		</header>
	);
}

function KpiStrip(props: {
	levelsConfigured: number;
	maxDepth: number;
	licenseOk: boolean | null;
	projectedTotal: number;
}) {
	const licenseLabel =
		props.licenseOk === null ? "—" : props.licenseOk ? "Gated on" : "Gated off";
	const cells = [
		{ label: "Levels configured", value: formatInteger(props.levelsConfigured) },
		{ label: "Max depth", value: formatInteger(props.maxDepth) },
		{ label: "License status", value: licenseLabel },
		{ label: "Projected payout", value: formatCents(props.projectedTotal) },
	];
	return (
		<section
			aria-label="Multi-level statistics"
			className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
			data-testid="kpi-strip"
		>
			{cells.map((c) => (
				<div key={c.label} className={`${CARD} flex flex-col gap-1 p-4`}>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{c.label}
					</p>
					<p className="text-2xl font-semibold tabular-nums text-zinc-900">
						{c.value}
					</p>
				</div>
			))}
		</section>
	);
}

function ProgramCard(props: {
	offerId: string;
	setOfferId: (s: string) => void;
	maxDepth: number;
	setMaxDepth: (n: number) => void;
	bpsRaw: string;
	setBpsRaw: (s: string) => void;
	parsedBps: number[];
	bpsValid: boolean;
	licenseOk: boolean;
	setLicenseOk: (b: boolean) => void;
	active: boolean;
	setActive: (b: boolean) => void;
	program: Program | null;
	loading: boolean;
	busy: boolean;
	onSave: () => void;
	onLicenseToggle: () => void;
	onDelete: () => void;
}) {
	return (
		<section className={`${CARD} flex flex-col`} data-testid="program-card">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">
						Program configuration
					</h2>
					<p className="text-sm text-zinc-500">
						Bind a cascade of basis-point shares to an iGaming offer. At least{" "}
						{MIN_LEVELS_REQUIRED} levels are required.
					</p>
				</div>
			</header>

			<div className="flex flex-col gap-4 p-5">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-1">
						<label htmlFor="mlm-offer-id" className={LABEL}>
							Offer id
						</label>
						<input
							id="mlm-offer-id"
							name="offerId"
							type="text"
							className={INPUT}
							placeholder="offer_casino_001"
							value={props.offerId}
							onChange={(e) => props.setOfferId(e.currentTarget.value)}
							data-testid="offer-id-input"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="mlm-max-depth" className={LABEL}>
							Max depth (levels)
						</label>
						<input
							id="mlm-max-depth"
							name="maxDepth"
							type="number"
							min={MIN_LEVELS_REQUIRED}
							max={MAX_DEPTH}
							className={INPUT}
							value={props.maxDepth}
							onChange={(e) => {
								const v = Number.parseInt(e.currentTarget.value, 10);
								props.setMaxDepth(Number.isInteger(v) ? v : MIN_LEVELS_REQUIRED);
							}}
							data-testid="max-depth-input"
						/>
					</div>
				</div>

				<div className="flex flex-col gap-1">
					<label htmlFor="mlm-bps" className={LABEL}>
						Level bps (comma-separated, one per level)
					</label>
					<input
						id="mlm-bps"
						name="bps"
						type="text"
						className={INPUT}
						placeholder="500, 300, 100"
						value={props.bpsRaw}
						onChange={(e) => props.setBpsRaw(e.currentTarget.value)}
						data-testid="bps-input"
					/>
					<p className="text-xs text-zinc-500" data-testid="bps-preview">
						{props.parsedBps.length === 0
							? "Enter integer bps separated by commas."
							: props.parsedBps
									.map((b, i) => `L${i + 1}: ${formatBpsPct(b)}`)
									.join(" · ")}
					</p>
					{!props.bpsValid && props.bpsRaw.trim().length > 0 ? (
						<p className="text-xs text-red-600">
							Need at least {Math.max(MIN_LEVELS_REQUIRED, props.maxDepth)} valid entries (0–10000).
						</p>
					) : null}
				</div>

				<div className="flex flex-wrap items-center gap-4">
					<label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700">
						<input
							type="checkbox"
							checked={props.licenseOk}
							onChange={(e) => props.setLicenseOk(e.currentTarget.checked)}
							className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
							data-testid="license-ok-input"
						/>
						License gate on
					</label>
					<label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700">
						<input
							type="checkbox"
							checked={props.active}
							onChange={(e) => props.setActive(e.currentTarget.checked)}
							className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
							data-testid="active-input"
						/>
						Active
					</label>
				</div>

				<div className="flex flex-wrap items-center gap-2 border-t border-zinc-200 pt-4">
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={props.onSave}
						disabled={props.busy || !props.bpsValid || props.offerId.trim().length === 0}
						data-testid="save-program-btn"
					>
						{props.program ? "Update program" : "Create program"}
					</button>
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={props.onLicenseToggle}
						disabled={props.busy || !props.program}
						data-testid="license-toggle-btn"
					>
						{props.program?.license_ok
							? "Revoke license flag"
							: "Set license flag"}
					</button>
					<button
						type="button"
						className={BTN_DANGER}
						onClick={props.onDelete}
						disabled={props.busy || !props.program}
						data-testid="delete-program-btn"
					>
						Deactivate
					</button>
				</div>

				{props.loading ? (
					<p className="text-sm text-zinc-500">Loading program.</p>
				) : props.program ? (
					<div
						className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-700"
						data-testid="program-summary"
					>
						<p>
							<span className="font-semibold">Program id:</span>{" "}
							<span className="font-mono">{props.program.id}</span>
						</p>
						<p>
							<span className="font-semibold">License:</span>{" "}
							{props.program.license_ok ? "on" : "off"}
							{" · "}
							<span className="font-semibold">Active:</span>{" "}
							{props.program.active ? "yes" : "no"}
						</p>
						<p>
							<span className="font-semibold">Levels:</span>{" "}
							{props.program.level_bps
								.map((b, i) => `L${i + 1}: ${formatBpsPct(b)}`)
								.join(" · ")}
						</p>
					</div>
				) : props.offerId.trim().length > 0 ? (
					<p className="text-sm text-zinc-500">
						No program yet for this offer. Configure levels and save to create one.
					</p>
				) : null}
			</div>
		</section>
	);
}

function UplineCard(props: {
	affiliateId: string;
	setAffiliateId: (s: string) => void;
	parentId: string;
	setParentId: (s: string) => void;
	data: UplineChainResponse | null;
	loading: boolean;
	busy: boolean;
	onSave: () => void;
}) {
	return (
		<section className={`${CARD} flex flex-col`} data-testid="upline-card">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">
						Upline registry
					</h2>
					<p className="text-sm text-zinc-500">
						Register an affiliate's parent. Cycles are rejected at save time.
					</p>
				</div>
			</header>
			<div className="flex flex-col gap-4 p-5">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-1">
						<label htmlFor="mlm-aff-id" className={LABEL}>
							Affiliate id
						</label>
						<input
							id="mlm-aff-id"
							name="affiliateId"
							type="text"
							className={INPUT}
							placeholder="aff_chain_child"
							value={props.affiliateId}
							onChange={(e) => props.setAffiliateId(e.currentTarget.value)}
							data-testid="upline-affiliate-input"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="mlm-parent-id" className={LABEL}>
							Parent id (blank for root)
						</label>
						<input
							id="mlm-parent-id"
							name="parentId"
							type="text"
							className={INPUT}
							placeholder="aff_chain_parent"
							value={props.parentId}
							onChange={(e) => props.setParentId(e.currentTarget.value)}
							data-testid="upline-parent-input"
						/>
					</div>
				</div>

				<div>
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={props.onSave}
						disabled={props.busy || props.affiliateId.trim().length === 0}
						data-testid="save-upline-btn"
					>
						Save upline
					</button>
				</div>

				{props.loading ? (
					<p className="text-sm text-zinc-500">Loading chain.</p>
				) : props.data && props.data.self ? (
					<div className="flex flex-col gap-2 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-700">
						<p>
							<span className="font-semibold">Depth from root:</span>{" "}
							{formatInteger(props.data.self.depth_from_root)}
						</p>
						<p data-testid="upline-chain">
							<span className="font-semibold">Chain:</span>{" "}
							{props.data.chain.length === 0
								? "root (no parent)"
								: props.data.chain.join(" -> ")}
						</p>
					</div>
				) : props.affiliateId.trim().length > 0 ? (
					<p className="text-sm text-zinc-500">
						No upline record yet for this affiliate.
					</p>
				) : null}
			</div>
		</section>
	);
}

function CascadeCard(props: {
	producerAffId: string;
	setProducerAffId: (s: string) => void;
	ngrInput: string;
	setNgrInput: (s: string) => void;
	ngrCents: number;
	lastCascade: CalculateResponse | null;
	busy: boolean;
	onCalculate: () => void;
	onCommit: () => void;
}) {
	return (
		<section className={`${CARD} flex flex-col`} data-testid="cascade-card">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">
						Cascade calculator
					</h2>
					<p className="text-sm text-zinc-500">
						Dry-run or commit a cascade against the configured program + upline
						tree. License gate must be on.
					</p>
				</div>
			</header>
			<div className="flex flex-col gap-4 p-5">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-1">
						<label htmlFor="mlm-producer-id" className={LABEL}>
							Producer affiliate id (level 0)
						</label>
						<input
							id="mlm-producer-id"
							name="producerAffId"
							type="text"
							className={INPUT}
							placeholder="aff_producer"
							value={props.producerAffId}
							onChange={(e) => props.setProducerAffId(e.currentTarget.value)}
							data-testid="producer-id-input"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="mlm-ngr" className={LABEL}>
							NGR (cents)
						</label>
						<input
							id="mlm-ngr"
							name="ngr"
							type="number"
							min={1}
							className={INPUT}
							value={props.ngrInput}
							onChange={(e) => props.setNgrInput(e.currentTarget.value)}
							data-testid="ngr-input"
						/>
						<p className="text-xs text-zinc-500">
							{props.ngrCents > 0
								? `= ${formatCents(props.ngrCents)}`
								: "Enter a positive integer."}
						</p>
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-2">
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={props.onCalculate}
						disabled={props.busy}
						data-testid="calculate-btn"
					>
						Dry-run
					</button>
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={props.onCommit}
						disabled={props.busy}
						data-testid="commit-btn"
					>
						Commit event
					</button>
				</div>

				{props.lastCascade ? (
					<CascadeResultTable levels={props.lastCascade.levels} total={props.lastCascade.total_payout_cents} />
				) : (
					<p className="text-sm text-zinc-500">
						Run a dry-run to preview the per-level breakdown.
					</p>
				)}
			</div>
		</section>
	);
}

function CascadeResultTable(props: {
	levels: readonly LevelPayout[];
	total: number;
}) {
	if (props.levels.length === 0) {
		return (
			<p className="text-sm text-zinc-500" data-testid="cascade-empty">
				No upline — producer is at the root. Nothing to cascade.
			</p>
		);
	}
	return (
		<div className="overflow-hidden rounded-[6px] border border-zinc-200">
			<table className="w-full text-left text-sm" data-testid="cascade-table">
				<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
					<tr>
						<th className="px-3 py-2">Level</th>
						<th className="px-3 py-2">Affiliate</th>
						<th className="px-3 py-2">Share</th>
						<th className="px-3 py-2 text-right">Payout</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-zinc-100">
					{props.levels.map((l) => (
						<tr key={`${l.level}-${l.affiliate_id}`} data-testid={`cascade-row-${l.level}`}>
							<td className="px-3 py-2 text-zinc-700">L{l.level}</td>
							<td className="px-3 py-2 font-mono text-zinc-900">{l.affiliate_id}</td>
							<td className="px-3 py-2 text-zinc-700 tabular-nums">
								{formatBpsPct(l.share_bps)}
							</td>
							<td className="px-3 py-2 text-right text-zinc-900 tabular-nums">
								{formatCents(l.payout_cents)}
							</td>
						</tr>
					))}
					<tr className="bg-zinc-50 text-zinc-900" data-testid="cascade-total-row">
						<td className="px-3 py-2 font-semibold" colSpan={3}>
							Total payout
						</td>
						<td className="px-3 py-2 text-right font-semibold tabular-nums">
							{formatCents(props.total)}
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	);
}

function EventsCard(props: {
	data: EventsResponse | null;
	loading: boolean;
	hasOffer: boolean;
}) {
	return (
		<section className={`${CARD} flex flex-col`} data-testid="events-card">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-base font-semibold text-zinc-900">
						Recent cascade events
					</h2>
					<p className="text-sm text-zinc-500">
						Up to 10 most recent commits for the selected offer.
					</p>
				</div>
			</header>
			<div className="p-5">
				{!props.hasOffer ? (
					<p className="text-sm text-zinc-500">
						Enter an offer id above to see its event history.
					</p>
				) : props.loading ? (
					<p className="text-sm text-zinc-500">Loading events.</p>
				) : !props.data || props.data.events.length === 0 ? (
					<p className="text-sm text-zinc-500">No events yet for this offer.</p>
				) : (
					<div className="overflow-hidden rounded-[6px] border border-zinc-200">
						<table className="w-full text-left text-sm" data-testid="events-table">
							<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
								<tr>
									<th className="px-3 py-2">Date</th>
									<th className="px-3 py-2">Producer</th>
									<th className="px-3 py-2">NGR</th>
									<th className="px-3 py-2">Levels</th>
									<th className="px-3 py-2 text-right">Total</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{props.data.events.map((ev) => (
									<tr key={ev.id} data-testid={`events-row-${ev.id}`}>
										<td className="px-3 py-2 text-zinc-700">
											{formatUnixDate(ev.created_at)}
										</td>
										<td className="px-3 py-2 font-mono text-zinc-900">
											{ev.producer_aff_id}
										</td>
										<td className="px-3 py-2 text-zinc-700 tabular-nums">
											{formatCents(ev.ngr_cents)}
										</td>
										<td className="px-3 py-2 text-zinc-700 tabular-nums">
											{formatInteger(ev.levels.length)}
										</td>
										<td className="px-3 py-2 text-right text-zinc-900 tabular-nums">
											{formatCents(ev.total_payout_cents)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</section>
	);
}
